import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { clientCreditsAPI, billsAPI } from '../services/api';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import { ArrowUpIcon, ClockIcon, DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import FcfaIcon from './FcfaIcon';
import { formatCurrency } from '../utils/helpers';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

/** Roles that may create a CR (credit top-up) bill; balance increases after payment is recorded. */
const REQUEST_CREDIT_TOPUP_ROLES = new Set(['MANAGER', 'ADMIN', 'SUPER', 'PESEUR', 'ACCOUNTANT']);

const ClientCreditModal = ({
  isOpen,
  onClose,
  client,
  onCreditUpdated,
  successBannerMessage = '',
  embedded = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const isClientUser = user?.role === 'CLIENT';
  const canRequestCreditTopup = REQUEST_CREDIT_TOPUP_ROLES.has(user?.role);
  const creditModalTitlePrefix = isClientUser
    ? t('clients.viewMyCredits')
    : canRequestCreditTopup
      ? t('clients.manageCredits')
      : t('clients.viewCredits');
  const [activeTab, setActiveTab] = useState('history');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!isClientUser && canRequestCreditTopup && successBannerMessage) {
      setActiveTab('request');
      return;
    }
    setActiveTab('history');
  }, [isOpen, isClientUser, canRequestCreditTopup, successBannerMessage]);

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');

  const { data: balanceData, loading: balanceLoading, refetch: refetchBalance } = useApi(
    () => {
      if (!client?.id) return Promise.resolve({ creditBalance: 0 });
      return clientCreditsAPI.getBalance(client.id);
    },
    [client?.id],
    { showErrorToast: false, enabled: !!client?.id && isOpen }
  );

  const { data: transactionsData, loading: transactionsLoading, refetch: refetchTransactions } = useApi(
    () => {
      if (!client?.id) return Promise.resolve({ transactions: [], pagination: {} });
      return clientCreditsAPI.getTransactions(client.id, { limit: 50 });
    },
    [client?.id, activeTab],
    { showErrorToast: false, enabled: !!client?.id && isOpen && activeTab === 'history' }
  );

  const {
    data: pendingBillsData,
    loading: pendingBillsLoading,
    refetch: refetchPendingBills,
  } = useApi(
    () => {
      if (!client?.id) return Promise.resolve({ bills: [] });
      return clientCreditsAPI.getPendingCreditTopupBills(client.id);
    },
    [client?.id, activeTab],
    { showErrorToast: false, enabled: !!client?.id && isOpen && activeTab === 'history' }
  );

  const pendingBills = pendingBillsData?.bills || [];

  const pendingListRemainingTotal = useMemo(() => {
    const rows = pendingBillsData?.bills || [];
    return rows.reduce((sum, inv) => sum + Math.max(0, Number(inv.remainingAmount || 0)), 0);
  }, [pendingBillsData]);

  const canDeleteOwnPendingCreditTopupBill = (inv) =>
    inv.billKind === 'CREDIT_TOPUP' &&
    inv.requestedByUserId &&
    user?.id &&
    inv.requestedByUserId === user.id &&
    ['PENDING', 'OVERDUE'].includes(inv.status) &&
    !inv.hasWaitingVerification &&
    Number(inv.paidAmount || 0) < 0.01;

  const handleDeletePendingCreditTopupBill = async (inv) => {
    if (!canDeleteOwnPendingCreditTopupBill(inv)) return;
    if (
      !window.confirm(
        t('clients.deletePendingCreditTopupBillConfirm', { billNumber: inv.billNumber })
      )
    ) {
      return;
    }
    setDeletingBillId(inv.id);
    try {
      await billsAPI.delete(inv.id);
      toast.success(t('clients.deletePendingCreditTopupBillSuccess'));
      refetchPendingBills();
      if (onCreditUpdated) {
        onCreditUpdated();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('clients.deletePendingCreditTopupBillError'));
    } finally {
      setDeletingBillId(null);
    }
  };

  const handleRequestCreditTopup = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('clients.creditAmountRequired'));
      return;
    }

    setRequestSubmitting(true);
    try {
      const res = await clientCreditsAPI.requestCreditTopupBill(client.id, {
        amount: parseFloat(amount),
        description: description || undefined,
        dueDate: dueDate || undefined,
      });
      const billNumber = res?.data?.bill?.billNumber;
      toast.success(
        billNumber
          ? t('clients.creditTopupBillCreated', { billNumber })
          : t('clients.creditTopupBillCreatedGeneric')
      );
      setAmount('');
      setDescription('');
      setDueDate('');
      refetchPendingBills();
      if (onCreditUpdated) {
        onCreditUpdated();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('clients.creditTopupBillError'));
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!adjustmentAmount || parseFloat(adjustmentAmount) === 0) {
      toast.error(t('clients.creditAdjustmentAmountRequired'));
      return;
    }
    if (!adjustmentDescription) {
      toast.error(t('clients.creditAdjustmentDescriptionRequired'));
      return;
    }

    try {
      await clientCreditsAPI.adjust(client.id, {
        amount: parseFloat(adjustmentAmount),
        description: adjustmentDescription,
      });
      toast.success(t('clients.creditAdjusted'));
      setAdjustmentAmount('');
      setAdjustmentDescription('');
      refetchBalance();
      if (activeTab === 'history') {
        refetchTransactions();
      }
      if (onCreditUpdated) {
        onCreditUpdated();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('clients.creditAdjustError'));
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'DEPOSIT':
        return 'text-green-600 bg-green-50';
      case 'PAYMENT':
        return 'text-red-600 bg-red-50';
      case 'REFUND':
        return 'text-blue-600 bg-blue-50';
      case 'ADJUSTMENT':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (!client) return null;

  const content = (
    <div className="space-y-4 sm:space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 sm:p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">{t('clients.currentBalance')}</p>
              <div className="flex items-center gap-2">
                <FcfaIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                <span className="text-2xl sm:text-3xl font-bold text-green-700">
                  {balanceLoading ? '...' : formatCurrency(balanceData?.creditBalance || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {successBannerMessage ? (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-sm font-medium text-green-900">{successBannerMessage}</p>
          </div>
        ) : null}

        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 flex-wrap gap-y-2">
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('clients.transactionHistory')}
            </button>
            {!isClientUser && canRequestCreditTopup && (
              <button
                type="button"
                onClick={() => setActiveTab('request')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'request'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('clients.requestCreditTopup')}
              </button>
            )}
            {user?.role === 'ADMIN' && (
              <button
                type="button"
                onClick={() => setActiveTab('adjust')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'adjust'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('clients.adjustCredit')}
              </button>
            )}
          </nav>
        </div>

        {!isClientUser && canRequestCreditTopup && activeTab === 'request' && (
          <form onSubmit={handleRequestCreditTopup} className="space-y-4">
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              {t('clients.requestCreditTopupHint')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.amount')} *
              </label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
                className="min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('bills.dueDate')} ({t('common.optional')})
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.description')}
              </label>
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('clients.creditTopupDescriptionPlaceholder')}
                className="min-h-[44px]"
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
              <Button variant="outline" type="button" onClick={onClose} className="w-full sm:w-auto">
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={requestSubmitting} className="w-full sm:w-auto">
                <ArrowUpIcon className="h-4 w-4 mr-2" />
                {t('clients.requestCreditTopupSubmit')}
              </Button>
            </div>
          </form>
        )}

        {activeTab === 'adjust' && user?.role === 'ADMIN' && (
          <form onSubmit={handleAdjust} className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">{t('clients.adjustmentWarning')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.adjustmentAmount')} * (positive to add, negative to subtract)
              </label>
              <Input
                type="number"
                step="0.01"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.description')} *
              </label>
              <Input
                type="text"
                value={adjustmentDescription}
                onChange={(e) => setAdjustmentDescription(e.target.value)}
                placeholder={t('clients.adjustmentDescriptionPlaceholder')}
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" type="button" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="warning">
                {t('clients.adjustCredit')}
              </Button>
            </div>
          </form>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {pendingBillsLoading && (
              <div className="py-4 text-center text-sm text-gray-500">{t('common.loading')}</div>
            )}

            {!pendingBillsLoading && pendingBills.length > 0 && (
              <>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                    {t('clients.pendingCreditTopupBills')}
                  </h4>
                  <p className="text-xs text-gray-500 mb-2">{t('clients.pendingCreditTopupBillsHint')}</p>
                  <div className="space-y-2 mb-6">
                    {pendingBills.map((inv) => (
                      <div
                        key={inv.id}
                        className="border border-amber-200 bg-amber-50/80 rounded-lg p-3 sm:p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-mono text-sm font-medium text-gray-900">
                                {inv.billNumber}
                              </span>
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                {inv.billKind === 'CREDIT_TOPUP'
                                  ? t('clients.billKindLabelCreditTopup')
                                  : t('clients.billKindLabelStandard')}
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  inv.status === 'OVERDUE'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {t(`bills.${String(inv.status || 'PENDING').toLowerCase()}`, {
                                  defaultValue: inv.status,
                                })}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">
                              {t('bills.dueDate')}: {format(new Date(inv.dueDate), 'dd/MM/yyyy')}
                            </p>
                            {Number(inv.totalAmount) > 0.009 && (
                              <p className="text-xs text-gray-600 mt-1">
                                <span className="font-medium text-gray-700">{t('bills.totalAmount')}:</span>{' '}
                                <span className="tabular-nums font-semibold text-gray-900">
                                  {formatCurrency(inv.totalAmount)}
                                </span>
                              </p>
                            )}
                            {inv.paidAmount > 0.01 && (
                              <p className="text-xs text-gray-600 mt-1">
                                {t('clients.pendingCreditTopupPaidPart')}:{' '}
                                {formatCurrency(inv.paidAmount)}
                              </p>
                            )}
                            {Number(inv.amountAwaitingVerification) > 0.01 && (
                              <p className="text-xs text-amber-800 mt-1">
                                {t('clients.amountAwaitingVerificationLine', {
                                  amount: formatCurrency(inv.amountAwaitingVerification)
                                })}
                              </p>
                            )}
                            {Number(inv.amountRejected) > 0.01 && (
                              <p className="text-xs text-red-700 mt-1">
                                {t('clients.amountRejectedLine', {
                                  amount: formatCurrency(inv.amountRejected),
                                  count: Number(inv.rejectedPaymentCount || 0)
                                })}
                              </p>
                            )}
                          </div>
                          <div className="text-left sm:text-right">
                            {inv.remainingAmount > 0.009 ? (
                              <>
                                <p className="text-xs text-gray-600">{t('clients.pendingCreditTopupRemaining')}</p>
                                <p className="text-lg font-bold text-amber-900 tabular-nums">
                                  {formatCurrency(inv.remainingAmount)}
                                </p>
                              </>
                            ) : null}
                            {inv.hasWaitingVerification ? (
                              <p className="text-xs text-amber-800 mt-1">
                                {inv.hasAwaitingReverification
                                  ? t('clients.pendingBillPaymentAwaitingReverification')
                                  : t('clients.pendingBillPaymentAwaitingVerification')}
                              </p>
                            ) : null}
                            {inv.hasRejectedPayments ? (
                              <p className="text-xs text-red-700 mt-1">
                                {t('clients.pendingBillPaymentRejected')}
                              </p>
                            ) : null}
                            {inv.billKind === 'CREDIT_TOPUP' ? (
                              <p className="text-xs text-amber-800 mt-1">{t('clients.creditNotYetApplied')}</p>
                            ) : null}
                            {canDeleteOwnPendingCreditTopupBill(inv) ? (
                              <div className="mt-2 flex justify-end sm:justify-start">
                                <Button
                                  type="button"
                                  variant="danger"
                                  size="sm"
                                  loading={deletingBillId === inv.id}
                                  disabled={deletingBillId !== null}
                                  onClick={() => handleDeletePendingCreditTopupBill(inv)}
                                  className="inline-flex items-center gap-1"
                                >
                                  <TrashIcon className="h-4 w-4 shrink-0" />
                                  {t('clients.deletePendingCreditTopupBill')}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 sm:p-4 space-y-3">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{t('clients.pendingInvoicesTotalListed')}</span>
                    <span className="text-base font-bold text-amber-900 tabular-nums inline-flex items-center gap-1">
                      <FcfaIcon className="h-4 w-4 shrink-0" />
                      {formatCurrency(pendingListRemainingTotal)}
                    </span>
                  </div>
                </div>
              </>
            )}

            <h4 className="text-sm font-semibold text-gray-900 border-t border-gray-200 pt-4">
              {t('clients.creditMovementHistory')}
            </h4>

            {transactionsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-xs sm:text-sm text-gray-600">{t('common.loading')}</p>
              </div>
            ) : transactionsData?.transactions?.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {transactionsData.transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <div className="flex-1 w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${getTransactionTypeColor(transaction.type)}`}
                          >
                            {t(`clients.creditType.${transaction.type}`)}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-600">
                            {formatDateTime(transaction.createdAt)}
                          </span>
                        </div>
                        {transaction.description && (
                          <p className="text-xs sm:text-sm text-gray-700 mb-1 break-words">
                            {transaction.description}
                          </p>
                        )}
                        {transaction.bill && (
                          <p className="text-xs text-gray-500">
                            {t('clients.bill')}: {transaction.bill.billNumber}
                          </p>
                        )}
                        {transaction.creator && (
                          <p className="text-xs text-gray-500">
                            {t('clients.processedBy')}: {transaction.creator.firstName}{' '}
                            {transaction.creator.lastName}
                          </p>
                        )}
                      </div>
                      <div className="text-right sm:text-left w-full sm:w-auto">
                        <span
                          className={`text-base sm:text-lg font-bold ${
                            transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.amount > 0 ? '+' : ''}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ClockIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-400" />
                <p className="text-sm sm:text-base">{t('clients.noTransactions')}</p>
              </div>
            )}
            <div className="flex justify-end pt-4">
              <Button variant="outline" type="button" onClick={onClose} className="w-full sm:w-auto">
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${creditModalTitlePrefix} - ${client.companyName}`}
      size="large"
    >
      {content}
    </Modal>
  );
};

export default ClientCreditModal;
